// The repo Settings tab's test-send (#195) — the state half of the webhook
// rows, split from the tab the same way `createToast` / `createMachineRemove`
// are: an in-flight set with a re-entrancy guard and a graded outcome is state,
// not rendering.
//
// In-flight sends are keyed BY binding id, never one "which row is testing"
// slot. Two bindings on a repo are two independent sends, and a shared slot
// re-enables row A's button the moment row B starts — a second signed delivery
// at an endpoint the operator asked to poke once.

import { t } from '../i18n';
import { createKeyedMutation } from './keyed-mutation';
import type { ToastTone } from './toast';
import { gradeWebhookTest, testWebhook, type WebhookTestOutcome } from './webhooks';

/** Outcome → the tone its notice carries. "Queued for retry" is not a success. */
const TONE: Record<WebhookTestOutcome, ToastTone> = {
  delivered: 'success',
  rejected: 'warning',
  queued: 'info',
};

export type WebhookTestController = {
  /** True while THIS binding's test-send is in flight. */
  readonly isTesting: (id: string) => boolean;
  /** Fire a test-send. A second call for the same binding mid-flight is dropped. */
  readonly run: (id: string) => void;
};

/**
 * Test-send state for one surface. `notify` is the surface's own `<Toast/>`
 * (`createToast`), so the tab keeps the rendering and this keeps the write.
 * Every outcome speaks — including the failure, since a destructive-adjacent
 * action that silently no-ops reads as a dead button.
 */
export function createWebhookTest(input: {
  readonly token: string | null;
  readonly notify: (message: string, tone: ToastTone) => void;
}): WebhookTestController {
  // `createKeyedMutation` owns the per-binding lock. It used to be split across
  // two functions here — `run` READ the flag, `send` WROTE it — so the guard
  // could be forgotten at a third entry point without either half looking wrong.
  const send = createKeyedMutation({
    fallback: t('repo_detail.settings_test_failed'),
    onError: (message) => input.notify(message, 'error'),
  });

  return {
    isTesting: send.isBusy,
    run: (id) => {
      void send.run(id, async () => {
        const { delivery } = await testWebhook(id, input.token);
        const outcome = gradeWebhookTest(delivery);
        input.notify(
          t(`repo_detail.settings_test_${outcome}`, { code: delivery.status_code ?? '' }),
          TONE[outcome],
        );
      });
    },
  };
}
